import React from 'react';
import { Box } from '@chakra-ui/react';
import { IconButton } from '@/components/ui/icon-button';
import { LuPlus } from 'react-icons/lu';
import { Tooltip } from '@/components/ui/tooltip.tsx';
import { BOTTOM_BAR_SHADOW } from '@/components/ui/shadows.ts';
import {
	COMPOSER_ADD_BUTTON_SIZE,
	COMPOSER_TOOLTIP_CLOSE_DELAY_MS,
	COMPOSER_TOOLTIP_OPEN_DELAY_MS,
	TRANSITION_ADD_BUTTON_ROTATE,
} from './composerConstants.ts';

interface ComposerAddButtonProps {
	isOpen: boolean;
	/** When collapsed, a small dot hints there's an unsent draft in the composer. */
	hasPendingInput?: boolean;
	disabled?: boolean;
	onClick: () => void;
	boxShadow?: string;
	disableTransition?: boolean;
}

/**
 * The circular control left of the bar. A single plus that rotates 135° into an
 * X — one button morphing to show the state change, rather than swapping icons.
 */
const ComposerAddButton: React.FC<ComposerAddButtonProps> = (props) => {
	const { isOpen, hasPendingInput, disabled, onClick, boxShadow = BOTTOM_BAR_SHADOW, disableTransition } = props;
	const showDot = !isOpen && !!hasPendingInput;
	const rotateTransition = disableTransition ? 'none' : TRANSITION_ADD_BUTTON_ROTATE;

	return (
		<Tooltip
			openDelay={COMPOSER_TOOLTIP_OPEN_DELAY_MS}
			closeDelay={COMPOSER_TOOLTIP_CLOSE_DELAY_MS}
			content={isOpen ? 'Close' : 'Add to Bundle'}
			positioning={{ placement: 'top' }}
		>
			<IconButton
				type="button"
				aria-label={isOpen ? 'Close add menu' : 'Add to Bundle'}
				aria-expanded={isOpen}
				onClick={onClick}
				// Don't steal focus from the composer on click — focus stays on the ask field until
				// the note editor takes it, so the bar's :focus-within (border + shadow) never drops
				// mid-transition. Keyboard focus/activation is unaffected.
				onMouseDown={(e) => e.preventDefault()}
				disabled={disabled}
				flexShrink={0}
				boxSize={`${COMPOSER_ADD_BUTTON_SIZE}px`}
				position="relative"
				bg={isOpen ? 'brand.700' : 'yellow.500'}
				color={isOpen ? 'white' : 'brand.700'}
				boxShadow={boxShadow}
				border="1px solid"
				borderColor={isOpen ? 'transparent' : 'border.default'}
				transition={
					disableTransition ? 'none' : 'background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease'
				}
				_hover={{ bg: 'brand.700', color: 'yellow.500' }}
				_disabled={{ opacity: 0.5, cursor: 'default' }}
			>
				<Box
					display="flex"
					alignItems="center"
					justifyContent="center"
					transform={isOpen ? 'rotate(135deg)' : 'rotate(0deg)'}
					transition={rotateTransition}
					willChange="transform"
					css={{
						// IconButton's recipe sizes the inner svg to a fixed em, so the
						// react-icons `size` prop is ignored — force it here instead.
						'& svg': { width: '26px', height: '26px' },
						'@media (prefers-reduced-motion: reduce)': { transition: 'none' },
					}}
				>
					<LuPlus strokeWidth={2} />
				</Box>
				{showDot && (
					<Box position="absolute" top="9px" right="9px" boxSize="8px" borderRadius="full" bg="brand.700" />
				)}
			</IconButton>
		</Tooltip>
	);
};

export default ComposerAddButton;
